requestAnimationFrame(() => {
  themeBase.onDOMContentLoadedTrigger(() => {
    if (!customElements.get('deferred-media')) {
      class DeferredMedia extends HTMLElement {
        constructor() {
          super();
          this.onMediaReady = this.onMediaReady.bind(this);
          this.initialize();
        }

        initialize() {
          this.poster = this.querySelectorAll('[id^="Deferred-Poster-"], .rvideo-component__poster');
          if (!this.poster.length) return;

          this.threshold = 200;
          this.loadContentHandler = this.loadContent.bind(this);

          this.poster.forEach(element => element.addEventListener('click', this.loadContentHandler));

          if (this.dataset.preload) {
            this.checkViewportHandler = this.checkViewport.bind(this);

            themeBase.onWindowLoadTrigger(() => {
              ['throttle5fpsMove', 'MutationObserverLoaded', 'updateStickySidebar']
              .forEach(event => window.addEventListener(event, this.checkViewportHandler));
              this.checkViewport();
            });
          }
        }

        getTemplateMedia() {
          const template = this.querySelector('template');
          if (!template?.content?.firstElementChild) return null;

          const content = document.createElement('div');
          content.appendChild(template.content.firstElementChild.cloneNode(true));

          return content.querySelector('video, model-viewer, iframe');
        }

        getInlineMedia() {
          return this.querySelector('video, model-viewer, iframe');
        }

        getMedia() {
          const templateMedia = this.getTemplateMedia();

          return {
            fromTemplate: Boolean(templateMedia),
            media: templateMedia || this.getInlineMedia()
          };
        }

        shouldActivateIframe() {
          if (!this.iframe) return false;

          const currentSrc = this.iframe.getAttribute('src');

          return !this.iframe.dataset.src || (currentSrc && currentSrc !== 'about:blank');
        }

        prepareMedia() {
          if (!this.media) return;

          if (this.isVideoTag) {
            let shouldLoad = this.media.readyState === 0;
            this.media.preload = 'auto';

            if (this.media.autoplay) {
              this.media.muted = true;
            }

            if (this.media.dataset.src && (!this.media.currentSrc || window.safari)) {
              this.media.src = this.media.dataset.src;
              shouldLoad = true;
            }

            if (shouldLoad && typeof this.media.load === 'function') {
              this.media.load();
            }

            return;
          }

          if (this.media.tagName === 'IFRAME' && this.media.dataset.src) {
            const currentSrc = this.media.getAttribute('src');

            if (!currentSrc || currentSrc === 'about:blank') {
              this.media.setAttribute('src', this.media.dataset.src);
            }
          }
        }

        waitForMediaReady() {
          if (!this.media) return;

          if (!this.isVideoTag) {
            this.onMediaReady();
            return;
          }

          ['loadeddata', 'canplay', 'playing'].forEach(event => {
            this.media.addEventListener(event, this.onMediaReady, {
              once: true
            });
          });

          if (this.media.readyState >= 2) {
            this.onMediaReady();
          }
        }

        onMediaReady() {
          if (this.mediaReady) return;
          this.mediaReady = true;

          if (this.onMediaLoadeddata) {
            this.onMediaLoadeddata();
          }

          let poster = this.querySelector('.rvideo-component__poster');

          if (poster) {
            poster.remove();
            poster = null;
          }

          if (this.isVideoTag && this.media.autoplay) {
            this.media.play()?.catch(() => {});
          }
        }

        loadContent() {
          if (this.getAttribute('loaded')) return;

          const {
            media,
            fromTemplate
          } = this.getMedia();
          if (!media) return;

          this.media = media;
          this.isVideoTag = this.media && this.media.tagName === 'VIDEO';
          this.setAttribute('loaded', true);

          if (fromTemplate) {
            this.appendChild(this.media);
          }

          this.mediaReady = false;
          this.prepareMedia();
          this.waitForMediaReady();

          this.poster.forEach(element => element.removeEventListener('click', this.loadContentHandler));
        }

        checkViewport() {
          if (!themeBase.isInViewport({
              bounds: themeBounds.get(this),
              threshold: this.threshold
            })) {
            return;
          }

          this.loadContent();
          ['throttle5fpsMove', 'MutationObserverLoaded', 'updateStickySidebar']
          .forEach(event => window.removeEventListener(event, this.checkViewportHandler));
        }
      }

      window.DeferredMedia = DeferredMedia;
      customElements.define('deferred-media', DeferredMedia);

      class RvideoComponent extends DeferredMedia {
        constructor() {
          super();
        }

        connectedCallback() {
          let isMediaContentLoaded = false;

          this.onMediaLoadeddata = () => {
            isMediaContentLoaded = true;
          };

          themeBase.onWindowLoadTrigger(() => {
            setTimeout(() => {
              if (isMediaContentLoaded) return;
              this.querySelector('.rvideo-component__poster')?.classList.remove('hidden');
            }, 1000);
          });

          if (this.querySelector('template')) {
            return;
          }

          this.initHandlers();
        }

        initHandlers() {
          if (this.handlersInitialized) return;

          this.video = this.querySelector('video');

          if (this.video) {
            this.handlersInitialized = true;
            this.addEventListener('click', () => {
              if (this.video.paused) {
                if (!this.video.muted) {
                  window.pauseAllMedia();
                }

                this.video.play();
                this.classList.remove('rvideo-component--paused');
              } else {
                this.video.pause();
                this.classList.add('rvideo-component--paused');
              }
            });

            if (!this.video.muted) {
              this.closest('slider-component')?.addEventListener('slideChanged', () => this.video.pause());
            }

            return;
          }

          this.iframe = this.querySelector('iframe');

          if (this.iframe && this.shouldActivateIframe()) {
            this.handlersInitialized = true;
            this.classList.add('rvideo-component--iframe');
            this.classList.remove('rvideo-component--paused');
          }
        }

        loadContent() {
          super.loadContent();

          this.initHandlers();

          if (!this.isVideoTag) {
            this.classList.remove('rvideo-component--paused');
            return;
          }
        }
      }

      customElements.define('rvideo-component', RvideoComponent);

      scriptController.scripts.push('deferred-media.js');
    }
  });
});